/* eslint-disable @typescript-eslint/no-unsafe-type-assertion, react/destructuring-assignment, @typescript-eslint/unbound-method, react/jsx-indent, react/jsx-wrap-multilines, no-else-return, max-len -- Preserve the existing directory tab and lifecycle flow during this batch. */
import React, { Component } from 'react';
import styled from 'styled-components';
import type { DirectoryPageViewModel } from '@lib/page-data/directory.map';
import type { Locale } from '@lib/locale';
import type { PageInjectedProps } from './Page';
import {
  STORE_DIRECTORY_SLUG,
  CATEGORIES_DIRECTORY_SLUG,
  DATA_TRACK_TYPE_INTERNAL,
} from '../../../../lib/constants';
import { trackAnalyticsNew } from '../../../../lib/utils';
import MaxWidthLayout from '../../../templates/MaxWidthLayout';
import MediaQueryHelper from '../../../templates/MediaQueryHelper';
import ShopsDirectoryContent from '../ShopDirectoryPage/ShopsDirectoryContent';
import CategoriesDirectoryContent from '../CategoriesDirectoryPage/CategoriesDirectoryContent';
import { GoogleAdSlot } from '../GoogleAdSlot/GoogleAdSlot';
import TabGroupImpl from '../shared/TabGroup';

type DirectoryTab = { id: string; label: string; analytics: object };
const TabGroup = TabGroupImpl as React.ComponentType<{
  tabs: DirectoryTab[];
  activeTab: string;
  onTabChange: (slug: string) => void;
  ariaLabel: string;
  idPrefix: string;
  isMobile?: boolean;
}>;

const ScreenReaderOnlyH1 = styled.h1`
  position:absolute;
  left:-10000px;
  top:auto;
  width:1px;
  height:1px;
  overflow:hidden;
`;

const TABS = [STORE_DIRECTORY_SLUG, CATEGORIES_DIRECTORY_SLUG];
const TAB_ANALYTICS_NAMES = {
  [STORE_DIRECTORY_SLUG]: 'stores',
  [CATEGORIES_DIRECTORY_SLUG]: 'categories',
};

type Props = Pick<DirectoryPageViewModel, 'shopOrder' | 'globalPageConfig' | 'globalShopConfig' | 'globalCategoryConfig' | 'allCategories'> & {
  locale: Locale;
  googleAd: DirectoryPageViewModel['directoryAdToDisplay'];
} & Partial<Pick<PageInjectedProps, 'userData' | 'pageStateModifiers' | 'userShopsData'>>;
type State = { activeTab: string };

class DirectoryContent extends Component<Props, State> {
  directoryData: Record<string, string | null | undefined>;

  constructor(props: Props) {
    super(props);

    this.state = {
      activeTab: '',
    };

    const {
      globalPageConfig = {},
    } = this.props;

    const {
      shopsDirectoryTabTitle = 'Stores',
      categoriesDirectoryTabTitle = 'Categories',
    } = globalPageConfig;

    this.directoryData = {};
    this.directoryData[STORE_DIRECTORY_SLUG] = shopsDirectoryTabTitle;
    this.directoryData[CATEGORIES_DIRECTORY_SLUG] = categoriesDirectoryTabTitle;

    this.setActiveTab = this.setActiveTab.bind(this);
  }

  componentDidMount() {
    const url = window.location.href;
    const hashSlug = url.slice(url.indexOf('#') + 1);
    const hashSlugToUse = TABS.includes(hashSlug) ? hashSlug : STORE_DIRECTORY_SLUG;

    // default to shops tab if the hash is not a valid slug
    if (hashSlugToUse !== this.state.activeTab) {
      this.setState({ activeTab: hashSlugToUse });
      if (window) {
        window.location.hash = hashSlugToUse;
      }
    }
  }

  componentDidUpdate() {
    if (window) {
      window.location.hash = this.state.activeTab;
    }
  }

  setActiveTab(activeSlug: string) {
    this.setState({ activeTab: activeSlug });
  }

  render() {
    const {
      userData,
      pageStateModifiers,
      globalPageConfig,
      shopOrder,
      locale,
      allCategories,
      globalCategoryConfig = {},
      globalShopConfig = {},
      googleAd,
    } = this.props;

    const { allShopsHeadingLabel = 'All Shops' } = globalPageConfig;

    const tabsToRender = TABS
      .map((tabSlug) => {
        const tabTitle = this.directoryData[tabSlug];

        // short-circuit if no tab data for this tab
        if (!tabTitle) {
          return null;
        }

        return {
          id: tabSlug,
          label: tabTitle,
          analytics: {
            ...trackAnalyticsNew({
              url: '',
              location: 'page',
              name: TAB_ANALYTICS_NAMES[tabSlug],
            }),
            dataTrackType: DATA_TRACK_TYPE_INTERNAL,
          },
        };
      })
      .filter((tab): tab is NonNullable<typeof tab> => tab !== null);

    const compToRender = (isMobile: boolean) => {
      if (this.state.activeTab === STORE_DIRECTORY_SLUG) {
        return (
          <ShopsDirectoryContent
            userData={userData}
            pageStateModifiers={pageStateModifiers}
            globalPageConfig={globalPageConfig}
            globalShopConfig={globalShopConfig}
            shopOrder={shopOrder}
            locale={locale}
            isMobile={isMobile}
          />);
      } else if (this.state.activeTab === CATEGORIES_DIRECTORY_SLUG) {
        return (
          <CategoriesDirectoryContent
            locale={locale}
            allCategories={allCategories}
            globalPageConfig={globalPageConfig}
            globalCategoryConfig={globalCategoryConfig}
            isMobile={isMobile}
          />);
      }
      return null;
    };

    const desktopPage = (
      <MaxWidthLayout>
        <TabGroup
          tabs={tabsToRender}
          activeTab={this.state.activeTab}
          onTabChange={this.setActiveTab}
          ariaLabel="Directory tabs"
          idPrefix="directory-tabs"
        />
        {compToRender(false)}
      </MaxWidthLayout>
    );

    const mobilePage = (
      <div>
        <TabGroup
          tabs={tabsToRender}
          activeTab={this.state.activeTab}
          onTabChange={this.setActiveTab}
          isMobile
          ariaLabel="Directory tabs"
          idPrefix="directory-tabs-mobile"
        />
        {compToRender(true)}
      </div>
    );

    const renderComp = (
      <MaxWidthLayout>
        <ScreenReaderOnlyH1>{allShopsHeadingLabel}</ScreenReaderOnlyH1>

        {googleAd && googleAd.active && (
          <GoogleAdSlot adId={googleAd.id} key={googleAd.id} verticalMargin={12} />
        )}

        <MediaQueryHelper
          desktopChildren={desktopPage}
          mobileChildren={mobilePage}
        />
      </MaxWidthLayout>
    );

    return renderComp;
  }
}

export default DirectoryContent;
